import {
  IntegrationProvider,
  type ZoomContactCenterIntegration,
} from "@repo/app-types/entities/integration";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { FC } from "react";
import { Navigate, Outlet, useOutletContext } from "react-router";
import { useZoomContactCenterIntegration } from "../../../api/entities/zoomContactCenterIntegration.js";
import { appPaths } from "../../../app/appPaths.js";
import {
  PageBody,
  TabbedPageLayout,
} from "../../../components/layout/pageLayout.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { useSettingsSectionsPolicy } from "../settingsSectionsPolicy.js";
import { IntegrationLogo } from "./integrationLogo.js";

const ZOOM_TITLE = "Zoom Contact Center";

/**
 * What the detail page's tab(s) share: the Zoom CC integration and its load
 * state. Fetched once by the section shell and handed down through the router
 * `Outlet` so a tab doesn't re-subscribe.
 */
export interface ZoomContactCenterDetailOutletContext {
  integration: ZoomContactCenterIntegration | null;
  isLoading: boolean;
  isError: boolean;
  onReload: () => void;
}

export function useZoomContactCenterDetailOutletContext(): ZoomContactCenterDetailOutletContext {
  return useOutletContext<ZoomContactCenterDetailOutletContext>();
}

/**
 * The Zoom Contact Center integration detail page: a wider frame (like
 * `/settings/teammates`) hosting the **Settings** tab (the connection UI). The
 * other integrations keep the narrow, non-tabbed detail rendered by
 * `IntegrationSettingsSection`; only Zoom CC routes here, via its own literal
 * `zoom-contact-center` route.
 *
 * The "Failed imports" tab (the customer's own failed `zoom_recording` rows)
 * ships with the poll/import slice; until then this page is just the connection
 * form.
 */
export const ZoomContactCenterIntegrationDetailSection: FC = () => {
  const isZoomEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.ZoomCcIntegration,
  );
  const { usesRecruitingIntegrations } = useSettingsSectionsPolicy();

  // Recruiting companies get the recruiting integrations page and never have
  // Zoom CC; gate the fetch (and redirect below) the same way the shared
  // section's visibility does.
  const isAvailable = isZoomEnabled && !usesRecruitingIntegrations;
  const zoomStatus = useZoomContactCenterIntegration(isAvailable);
  const integration = zoomStatus.currentData?.integration ?? null;

  if (!isAvailable) {
    return <Navigate to={appPaths.settingsIntegrations.build()} replace />;
  }

  const context: ZoomContactCenterDetailOutletContext = {
    integration,
    isLoading: !zoomStatus.currentData && !zoomStatus.isError,
    isError: zoomStatus.isError && !zoomStatus.currentData,
    onReload: () => void zoomStatus.refetch(),
  };

  return (
    <TabbedPageLayout
      header={
        <PageHeader
          width={PageWidth.Wide}
          title={
            <span className="flex min-w-0 items-center gap-2">
              <IntegrationLogo
                provider={IntegrationProvider.ZOOM_CONTACT_CENTER}
              />
              <span className="truncate">{ZOOM_TITLE}</span>
            </span>
          }
        />
      }
    >
      <PageBody width={PageWidth.Wide}>
        <Outlet context={context} />
      </PageBody>
    </TabbedPageLayout>
  );
};
